import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useParams, useLocation } from "react-router-dom";
import { IS_STORE_HOST, RESERVED } from "@/lib/host";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PrintProvider } from "@/context/PrintContext";
import AppShell from "@/components/AppShell";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";
import POS from "@/pages/POS";
import Sales from "@/pages/Sales";
import Products from "@/pages/Products";
import Settings from "@/pages/Settings";
import Landing from "@/pages/Landing";
import Upgrade, { LockScreen } from "@/pages/Upgrade";
import AdminApp from "@/pages/admin/AdminApp";
import Team from "@/pages/Team";
import HowItWorks from "@/pages/HowItWorks";
import PriceTags from "@/pages/PriceTags";
import ImportProducts from "@/pages/ImportProducts";
import { OfflineEngine, OnlineOnly } from "@/components/Offline";
import { isLocked } from "@/context/AuthContext";
import { ContactProvider } from "@/context/ContactContext";
import Contact from "@/pages/Contact";
import Inventory from "@/pages/Inventory";
import Returns from "@/pages/Returns";
import Reseller from "@/pages/Reseller";
import { captureReferral } from "@/lib/referral";
import { lazy, Suspense } from "react";
import OnlineStore from "@/pages/OnlineStore";
import OnlineOrders from "@/pages/OnlineOrders";
import Reports from "@/pages/Reports";
import { ContentProvider } from "@/content/ContentContext";
import { MaintenanceGate } from "@/components/MaintenancePage";
import PublicReceipt from "@/pages/PublicReceipt";
import Customers from "@/pages/Customers";
import { NoAccess } from "@/components/NoAccess";
import RestaurantHome from "@/pages/restaurant/RestaurantHome";
import Tables from "@/pages/restaurant/Tables";
import OrderScreen from "@/pages/restaurant/OrderScreen";
import Kitchen from "@/pages/restaurant/Kitchen";
import Menu from "@/pages/restaurant/Menu";
import Reservations from "@/pages/restaurant/Reservations";

export const restaurantHome = (s) => (s?.permissions?.kitchen && !s?.permissions?.take_orders && !s?.permissions?.billing ? "/kitchen" : "/restaurant");
// Restaurant route guard: disallowed routes redirect to the user's home; retail/pharmacy keep their in-page No access handling.
function Guard({ any, rest, owner, children }) {
  const { session } = useAuth();
  const loc = useLocation();
  const isRest = session?.business?.business_type === "restaurant";
  if (rest && !isRest) return <Navigate to="/pos" replace />;
  if (!isRest) return children;
  const ok = owner ? session?.user?.role === "owner" : !any || any.some((p) => session?.permissions?.[p]);
  if (ok) return children;
  const home = restaurantHome(session);
  return home === loc.pathname ? <NoAccess what="this page" /> : <Navigate to={home} replace />;
}
function PosOrHome() {
  const { session } = useAuth();
  return session?.business?.business_type === "restaurant" ? <Navigate to={restaurantHome(session)} replace /> : <POS />;
}

const StoreFront = lazy(() => import("@/pages/store/StoreFront"));
const OrderTrack = lazy(() => import("@/pages/store/OrderTrack"));
const pubStore = (el) => <Suspense fallback={<Splash />}>{el}</Suspense>;

function HostStore({ track }) {
  const { slug } = useParams();
  if (RESERVED.has((slug || "").toLowerCase())) return <Navigate to="/" replace />;
  return pubStore(track ? <OrderTrack /> : <StoreFront />);
}

captureReferral();

function Splash() {
  return (
    <div className="grid min-h-[100dvh] place-items-center bg-slate-950" data-testid="app-loading">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 animate-pulse">Smart-in · loading</div>
    </div>
  );
}

function Protected({ children }) {
  const { session } = useAuth();
  if (session === null) return <Splash />;
  if (!session) return <Navigate to="/login" replace />;
  if (isLocked(session)) return <LockScreen />;
  return <AppShell>{children}</AppShell>;
}

function PublicOnly({ children }) {
  const { session } = useAuth();
  if (session === null) return <Splash />;
  if (session) return <Navigate to="/pos" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <ContentProvider>
      <AuthProvider>
        <PrintProvider>
        <ContactProvider>
          <OfflineEngine />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<MaintenanceGate><Landing /></MaintenanceGate>} />
              <Route path="/store/:slug" element={pubStore(<StoreFront />)} />
              <Route path="/store/:slug/order/:token" element={pubStore(<OrderTrack />)} />
              <Route path="/online-store" element={<Protected><Guard any={["online_orders"]}><OnlineOnly what="Online Store"><OnlineStore /></OnlineOnly></Guard></Protected>} />
              <Route path="/online-orders" element={<Protected><Guard any={["online_orders"]}><OnlineOrders /></Guard></Protected>} />
              <Route path="/reports" element={<Protected><Guard any={["reports"]}><OnlineOnly what="Reports"><Reports /></OnlineOnly></Guard></Protected>} />
              <Route path="/admin" element={<OnlineOnly what="Super admin"><AdminApp /></OnlineOnly>} />
              <Route path="/how-it-works" element={<MaintenanceGate><HowItWorks /></MaintenanceGate>} />
              <Route path="/reseller" element={<Reseller />} />
              <Route path="/contact" element={<MaintenanceGate><Contact /></MaintenanceGate>} />
              <Route path="/signup" element={<MaintenanceGate><PublicOnly><Signup /></PublicOnly></MaintenanceGate>} />
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/r/:token" element={<PublicReceipt />} />
              <Route path="/pos" element={<Protected><PosOrHome /></Protected>} />
              <Route path="/restaurant" element={<Protected><Guard rest any={["take_orders", "billing", "reports", "menu", "tables", "reservations"]}><OnlineOnly what="Restaurant"><RestaurantHome /></OnlineOnly></Guard></Protected>} />
              <Route path="/tables" element={<Protected><Guard rest any={["take_orders", "billing", "tables"]}><OnlineOnly what="Tables"><Tables /></OnlineOnly></Guard></Protected>} />
              <Route path="/tables/order/:orderId" element={<Protected><Guard rest any={["take_orders", "billing"]}><OnlineOnly what="Orders"><OrderScreen /></OnlineOnly></Guard></Protected>} />
              <Route path="/kitchen" element={<Protected><Guard rest any={["kitchen"]}><OnlineOnly what="Kitchen"><Kitchen /></OnlineOnly></Guard></Protected>} />
              <Route path="/menu" element={<Protected><Guard rest any={["menu"]}><OnlineOnly what="Menu"><Menu /></OnlineOnly></Guard></Protected>} />
              <Route path="/reservations" element={<Protected><Guard rest any={["reservations"]}><OnlineOnly what="Reservations"><Reservations /></OnlineOnly></Guard></Protected>} />
              <Route path="/customers" element={<Protected><Customers /></Protected>} />
              <Route path="/sales" element={<Protected><Guard any={["sales_history"]}><Sales /></Guard></Protected>} />
              <Route path="/products" element={<Protected><Products /></Protected>} />
              <Route path="/price-tags" element={<Protected><OnlineOnly what="Price tags"><PriceTags /></OnlineOnly></Protected>} />
              <Route path="/import" element={<Protected><OnlineOnly what="Import"><ImportProducts /></OnlineOnly></Protected>} />
              <Route path="/returns" element={<Protected><OnlineOnly what="Returns"><Returns /></OnlineOnly></Protected>} />
              <Route path="/inventory" element={<Protected><OnlineOnly what="Inventory"><Inventory /></OnlineOnly></Protected>} />
              <Route path="/settings" element={<Protected><Guard any={["settings"]}><Settings /></Guard></Protected>} />
              <Route path="/team" element={<Protected><Guard owner><OnlineOnly what="Team"><Team /></OnlineOnly></Guard></Protected>} />
              <Route path="/upgrade" element={<Protected><Upgrade /></Protected>} />
              {IS_STORE_HOST && <Route path="/:slug" element={<HostStore />} />}
              {IS_STORE_HOST && <Route path="/:slug/order/:token" element={<HostStore track />} />}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors closeButton toastOptions={{ className: "font-sans" }} />
        </ContactProvider>
        </PrintProvider>
      </AuthProvider>
      </ContentProvider>
    </div>
  );
}

export default App;
